import type { Sides } from "@project/emerge-types";
import { Matrix4, Shape, Vector2, Vector3 } from "three";
import { MiterType } from "../enum/MiterType";
import { ProfiledContourGeometry } from "../parts/ProfiledContourGeometry";
import { RectPropertiesBySide } from "../structure/RectProperties";
import { FinishedGeometryCache } from "./FinishedGeometryCache";

// Rails are cached after their longitudinal rotation, unlike regular profiles.
// Keep their immutable results and idle byte/count budget separate.
export const rackRailGeometryCache = new FinishedGeometryCache();

const MATERIAL_THICKNESS = 2.5;

export interface RackRailGeometryInput {
  readonly contour: readonly [Vector2, Vector2];
  readonly spacing: number;
  readonly miterType: MiterType;
}

interface RailDefinition {
  points?: readonly Vector3[];
  position?: Vector3;
  cutReference?: Sides;
  partSelection?: { spacing: number };
  miterType?: MiterType;
}

/** Snapshot only effective geometry values, in the original rail's local XY. */
export function rackRailGeometryInput(
  definition: RailDefinition | null | undefined,
): RackRailGeometryInput | null {
  if (
    !definition?.position ||
    definition.points?.length !== 2 ||
    definition.cutReference == null
  ) {
    return null;
  }
  const rect = RectPropertiesBySide[definition.cutReference];
  const [first, last] = definition.points;
  if (!rect || !first || !last) return null;
  const { horizontal, vertical } = rect;
  const { position } = definition;
  const local = (point: Vector3) =>
    new Vector2(
      point[vertical] - position[vertical],
      point[horizontal] - position[horizontal],
    );
  const contour: [Vector2, Vector2] = [local(first), local(last)];
  const spacing = definition.partSelection?.spacing || 25;
  if (
    ![spacing, ...contour.flatMap((point) => [point.x, point.y])].every(
      Number.isFinite,
    ) ||
    contour[0].equals(contour[1])
  ) {
    return null;
  }
  return {
    contour,
    spacing,
    miterType: definition.miterType ?? MiterType.NONE,
  };
}

export function rackRailGeometryKey(input: RackRailGeometryInput): string {
  return JSON.stringify([
    "rack-rail",
    input.spacing,
    input.contour.map((point) => [point.x, point.y]),
    input.miterType,
  ]);
}

/** Build once on a committed cache miss; cached geometries are never mutated. */
export function createRackRailGeometry(
  input: RackRailGeometryInput,
): ProfiledContourGeometry {
  const { spacing, contour, miterType } = input;
  const profile: [number, number][] = [
    [0, 0],
    [-spacing, 0],
    [-spacing, MATERIAL_THICKNESS],
    [-MATERIAL_THICKNESS, MATERIAL_THICKNESS],
    [-MATERIAL_THICKNESS, spacing],
    [0, spacing],
    [0, 0],
  ];
  const shape = new Shape(profile.map(([x, y]) => new Vector2(x, y)));
  const geometry = new ProfiledContourGeometry(
    shape,
    [...contour],
    miterType,
    false,
  );
  const direction = contour[1].clone().sub(contour[0]).normalize();
  const longitudinalAxis = new Vector3(direction.x, direction.y, 0).normalize();
  geometry.applyMatrix4(
    new Matrix4().makeRotationAxis(longitudinalAxis, -Math.PI / 2),
  );
  return geometry;
}
